import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faStop, faUser } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import whisperService from './services/whisperService';

function RecordingButton({ name, positiveWords }) {
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [recognition, setRecognition] = useState(null);
  const [error, setError] = useState(null);
  const [wordPercentage, setWordPercentage] = useState(0);
  const [useWhisper, setUseWhisper] = useState(true); // Toggle between Whisper and browser speech recognition
  const [processing, setProcessing] = useState(false);
  const [translation, setTranslation] = useState('');

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  const location = useLocation();
  const { patientId } = location.state || {};

  useEffect(() => {
    // Test Whisper service health on component mount
    const testWhisperHealth = async () => {
      try {
        const isHealthy = await whisperService.checkHealth();
        console.log('🎤 Whisper service health:', isHealthy ? 'Healthy' : 'Unavailable');
      } catch (error) {
        console.error('❌ Whisper health check failed:', error);
      }
    };

    testWhisperHealth();

    // Initialize the speech recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      // Speech recognition not supported, handle fallback
      console.error('Speech recognition not supported');
      return;
    }
    const newRecognition = new SpeechRecognition();
    newRecognition.continuous = true;
    newRecognition.interimResults = true;
    setRecognition(newRecognition);

    // Clean up the recognition object when the component unmounts
    return () => {
      newRecognition.stop();
    };
  }, []);

  const startWhisperRecording = async () => {
    try {
      setError(null);
      console.log('🎤 Starting Whisper recording...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        console.log('📊 Audio data available:', event.data.size, 'bytes');
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        console.log('⏹️ Recording stopped, processing audio...');
        setProcessing(true);
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        console.log('🔊 Audio blob created:', audioBlob.size, 'bytes');

        try {
          console.log('🚀 Calling Whisper API...');
          const result = await whisperService.transcribeAndTranslate(audioBlob);
          console.log('✅ Whisper result:', result);
          const newTranscript = transcript + ' ' + result.transcript;
          setTranscript(newTranscript);
          setTranslation(result.translation);

          calculateWordPercentage(newTranscript, positiveWords);
          saveTranscript(newTranscript);
        } catch (error) {
          setError('Failed to process audio with Whisper');
          console.error('❌ Whisper transcription error:', error);
        } finally {
          setProcessing(false);
        }
      };

      mediaRecorder.start();
      setRecording(true);
      console.log('✅ Recording started successfully');
    } catch (error) {
      setError('Failed to access microphone');
      console.error('❌ Recording error:', error);
    }
  };

  const stopWhisperRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  };

  const toggleRecording = () => {
    if (useWhisper) {
      if (!recording) {
        startWhisperRecording();
      } else {
        stopWhisperRecording();
      }
    } else {
      // Original browser speech recognition logic
      if (!recording) {
        recognition.start();
        setRecording(true);
      } else {
        recognition.stop();
        setRecording(false);
        console.log('Final transcript:', transcript);
        calculateWordPercentage(transcript, positiveWords);
        saveTranscript(transcript);
      }
    }
  };

  useEffect(() => {
    if (recognition) {
      recognition.onresult = (event) => {
        let finalTranscript = transcript; // Preserve the previous transcript
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript + ' '; // Add a space after each final transcript
          }
        }
        setTranscript(finalTranscript);

        // Calculate the percentage of positive words in the transcription
        if (!recording) {
          calculateWordPercentage(finalTranscript, positiveWords);
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setError(event.error);
      };
    }
  }, [recognition, positiveWords, recording, transcript]); // Include transcript in the dependency array


  const calculateWordPercentage = async (transcription, positiveWords) => {
    try {
      const response = await axios.post('http://20.40.42.218/api/calculate_word_percentage', {
        transcription: transcription,
      });
      console.log('API response:', response.data);
      setWordPercentage(response.data.word_percentage);
    } catch (error) {
      console.error('Error calculating word percentage:', error);
    }
  };
  const saveTranscript = async (transcript) => {
    try {
      await axios.post('http://20.40.42.218/api/save_transcript', {
        patientId: patientId,
        transcript: transcript,
      });
      console.log('Transcript saved successfully');
    } catch (error) {
      console.error('Error saving transcript:', error);
    }
  };

  return (
    <div className="container flex flex-col h-screen ">
     <nav className="bg-white w-full">
        <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
          <div className="relative flex items-center justify-between h-16">
            <div className="flex-shrink-0 flex items-center justify-center">
              <img className="h-8 w-auto" src="./logo.png" alt="Your Company" id='logo' />
            </div>
            <div className="flex-1 flex justify-center sm:justify-start">
              <div className="sm:hidden flex items-center">
                <button type="button" className="block text-gray-400 hover:text-white focus:text-white focus:outline-none">
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M4 6H20M4 12H20M4 18H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
              <div className="ml-3 relative">
                <button type="button" className="relative flex rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800" id="user-menu-button" aria-expanded="false" aria-haspopup="true">
                  <FontAwesomeIcon icon={faUser} className="text-2xl text-gray-500" />
                  <span className="text-xl font-semibold text-gray-500 mr-4 pl-3">Welcome Dr {name}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <main className="main flex flex-col items-center justify-center px-8 space-y-12 mt-10">
        <h2 className="text-3xl font-bold text-white">Recording Status</h2>
        {patientId && <p className="text-white">Patient ID: {patientId}</p>}

        {/* Service Toggle */}
        <div className="flex items-center space-x-4">
          <label className="text-white font-semibold">Speech Service:</label>
          <button
            onClick={() => setUseWhisper(!useWhisper)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              useWhisper
                ? 'bg-green-500 text-white'
                : 'bg-gray-300 text-gray-700'
            }`}
          >
            {useWhisper ? 'Whisper + DeepL' : 'Browser Speech'}
          </button>
        </div>

        {error && (
          <div className="bg-red-500 text-white p-4 rounded-lg">
            <p>Error: {error}</p>
          </div>
        )}

        {processing && (
          <div className="bg-blue-500 text-white p-4 rounded-lg">
            <p>Processing audio with Whisper...</p>
          </div>
        )}

        <button
          onClick={toggleRecording}
          disabled={processing}
          className={`recording-button flex items-center justify-center bg-white text-indigo-500 hover:bg-indigo-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 px-8 py-4 rounded-full shadow-md transition-colors duration-300 ${
            processing ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <FontAwesomeIcon icon={recording ? faStop : faMicrophone} className="text-3xl" />
          <span className="ml-4 text-xl">
            {processing ? 'Processing...' : recording ? 'Stop Recording' : 'Start Recording'}
          </span>
        </button>
      </main>

      <section className="transcription flex flex-col items-center justify-center px-8 py-8">
        <h2 className="text-xl font-semibold text-white mb-4">Transcription</h2>
        <textarea className="transcription-box w-full h-72 p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-lg bg-white text-xl" placeholder="Transcription will appear here" value={transcript} readOnly />
      </section>

      {useWhisper && translation && (
        <section className="translation flex flex-col items-center justify-center px-8 py-8">
          <h2 className="text-xl font-semibold text-white mb-4">Translation</h2>
          <textarea className="translation-box w-full h-32 p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-lg bg-white text-xl" placeholder="Translation will appear here" value={translation} readOnly />
        </section>
      )}
      {wordPercentage > 0 && (
        <div className="container conversation-percentage flex flex-col items-center justify-center px-8 py-8">
          <h2 className="text-xl font-semibold text-white mb-4">Conversation Percentage</h2>
          <div className="conversation-box flex flex-col px-8 py-8 w-full h-72 p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-lg bg-white">
            <p className="text-black text-3xl text-center mt-6">Conversation Matches {wordPercentage.toFixed(2)}%</p>
            <p className="text-black text-center mt-6">Congratulations! You have successfully completed the conversation.</p>
            {wordPercentage > 50 ? <p className="text-green-500 text-center text-2xl mt-6">You have made a positive impact on the patient.</p> : <p className="text-red-500 text-center text-2xl mt-6">Try to improve your conversation skills.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default RecordingButton;